import { type UseQueryOptions, type UseQueryResult, useMutation } from '@tanstack/react-query';
import { ASSIST_API, requestParams } from 'services/assist-api/axios';
import type { AssistQuery } from 'services/assist-api/query';
import { AssistQueryClient, useQueryAssist } from 'services/assist-api/query-client';
import {
  preparePatchTestInstanceResponseMark,
  preparePostTestInstanceResponseMark
} from './hooks.helpers';
import type {
  BaseTestInstanceResponseMark,
  PatchTestInstanceResponseMark,
  PostTestInstanceResponseMark,
  ReportTestInstanceResponseMark
} from './types';

const groups = [];
const include = 'testInstanceResponseMarkItems';

/**
 * Fetch test-instance-response-marks
 * @param params can be used to customise/limit the test-instance-response-marks retrieved
 * @returns the requested test-instance-response-marks
 */
const getTestInstanceResponseMarks: GetMany<BaseTestInstanceResponseMark> = async (params) =>
  (
    await ASSIST_API.get('test-instance-response-marks', {
      params: requestParams({}, params)
    })
  ).data;

/**
 * Fetch test-instance-response-mark (singular)
 * @param params can be used to customise/limit the test-instance-response-mark retrieved
 * @returns a single test-instance-response-mark
 */
const getTestInstanceResponseMark: GetOne<BaseTestInstanceResponseMark> = async (id, params) =>
  (await ASSIST_API.get(`test-instance-response-marks/${id}`, { params })).data;

/**
 * Hook implementation to fetch test-instance-response-marks
 *  - caches data
 *  - loading, error, etc
 * @param params additional options to filter the request
 * @returns test-instance-response-marks matching request
 */
export const useTestInstanceResponseMarks: AssistQuery.UseMany<BaseTestInstanceResponseMark> = (
  params,
  options
) =>
  useQueryAssist(
    ['test-instance-response-marks', params],
    () => getTestInstanceResponseMarks(params),
    options
  );

/**
 * Given an testInstanceResponseMarkId fetch and return the test instance response mark
 *  - only runs once testInstanceResponseMarkId is provided
 *  - fetches the one testInstanceResponseMark
 *  - caches data
 * @param id testInstanceResponseMarkId
 * @param params optional parameters to change requested data
 * @returns the matching testInstanceResponseMark
 */
export const useTestInstanceResponseMark: AssistQuery.UseOne<BaseTestInstanceResponseMark> = (
  id,
  params,
  options
) =>
  useQueryAssist(
    ['test-instance-response-marks', { id, ...params }],
    () => (id ? getTestInstanceResponseMark(id, params) : undefined),
    {
      ...options,
      enabled: !!id
    }
  );

/**
 * Creates methods to send DELETE requests to the api
 * @param options ReactQuery mutation options
 * @returns useMutation
 */
export const useDeleteTestInstanceResponseMark: AssistQuery.UseMutation<
  BaseTestInstanceResponseMark,
  number
> = (options) =>
  useMutation((id) => ASSIST_API.delete(`test-instance-response-marks/${id}`), {
    ...options,
    onSettled: (...args) => {
      AssistQueryClient.invalidateQueries(['test-instance-response-marks']);
      if (options?.onSettled) {
        options.onSettled(...args);
      }
    }
  });

/**
 * Remove test-instance-response-marks
 * - takes an array of ids creating a DELETE request per mark
 * @param options
 */
export const useDeleteTestInstanceResponseMarks: AssistQuery.UseMutations<undefined, number[]> = (
  options
) =>
  useMutation(
    (ids: number[]) =>
      Promise.allSettled(ids.map((id) => ASSIST_API.delete(`test-instance-response-marks/${id}`))),
    {
      ...options,
      onSettled: (...args) => {
        AssistQueryClient.invalidateQueries(['test-instance-response-marks']);
        if (options?.onSettled) {
          options.onSettled(...args);
        }
      }
    }
  );

/**
 * Create new test instance response mark (also saving response mark items)
 * - takes a test-instance-response-mark and creates a POST request
 * @param options
 */
export const usePostTestInstanceResponseMark: AssistQuery.UseMutation<
  PostTestInstanceResponseMark,
  PostTestInstanceResponseMark
> = (options) =>
  useMutation(
    (postTestInstanceResponseMark: PostTestInstanceResponseMark) =>
      ASSIST_API.post(
        'test-instance-response-marks',
        preparePostTestInstanceResponseMark(postTestInstanceResponseMark),
        {
          params: requestParams({ groups, include })
        }
      ),
    {
      ...options,
      onSettled: (...args) => {
        AssistQueryClient.invalidateQueries(['test-instance-response-marks']);
        if (options?.onSettled) {
          options.onSettled(...args);
        }
      }
    }
  );

/**
 * Updates a test-instance-response-mark
 * - takes a test-instance-response-mark and creates a PATCH request
 * @param options
 */
export const usePatchTestInstanceResponseMark: AssistQuery.UseMutation<
  BaseTestInstanceResponseMark,
  PatchTestInstanceResponseMark
> = (options) =>
  useMutation(
    ({ id, data }: PatchTestInstanceResponseMark) =>
      ASSIST_API.patch(
        `test-instance-response-marks/${id}`,
        preparePatchTestInstanceResponseMark(data),
        {
          params: requestParams({ groups, include })
        }
      ),
    {
      ...options,
      onSettled: (...args) => {
        AssistQueryClient.invalidateQueries(['test-instance-response-marks']);
        if (options?.onSettled) {
          options.onSettled(...args);
        }
      }
    }
  );

export const useReportTestInstanceResponseMark = (
  params: Pick<RequestParams, 'filters' | 'order' | 'pagination'>,
  options?: UseQueryOptions<
    unknown,
    RequestError,
    RequestResponse<ReportTestInstanceResponseMark[]>
  >
): UseQueryResult<RequestResponse<ReportTestInstanceResponseMark[]>, RequestError> =>
  useTestInstanceResponseMarks(
    {
      ...params,
      groups: [
        'testinstanceresponsemark-testinstanceresponse',
        'testinstanceresponsemark-holisticcomment',
        'testinstanceresponsemark-aimarked',
        'testinstanceresponsemark-aiengineservice',
        'aiengineservice-aiengine',
        'testinstanceresponsemark-markers',
        'testinstanceresponsemarker-user',
        'testinstanceresponsemark-markitems',
        'testinstanceresponsemarkitem-comment',
        'testinstanceresponsemarkitem-submittedscore',
        'testinstanceresponsemarkitem-testrubricitem'
      ],
      include: 'aiEngineService,markers,markItems'
    },
    options
  );
